import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';

export interface CookieConsent {
    analytics: boolean;
    timestamp: number;
}

@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    private readonly consentSubject = new BehaviorSubject<CookieConsent | null>(null);
    public readonly consent$: Observable<CookieConsent | null> = this.consentSubject.asObservable();

    private readonly showBannerSubject = new BehaviorSubject<boolean>(false);
    public readonly showBanner$: Observable<boolean> = this.showBannerSubject.asObservable();

    constructor(private readonly storageService: StorageService) {}

    async init(): Promise<void> {
        // On native platforms, always allow analytics and don't show banner
        if (Capacitor.isNativePlatform()) {
            this.consentSubject.next({ analytics: true, timestamp: Date.now() });
            this.showBannerSubject.next(false);
            return;
        }

        // Check if consent already exists
        const consent = await this.storageService.get<CookieConsent>('cookie-consent');
        if (consent) {
            this.consentSubject.next(consent);
            this.showBannerSubject.next(false);
        } else {
            // No consent yet, show banner
            this.showBannerSubject.next(true);
        }
    }

    async acceptCookies(): Promise<void> {
        const consent: CookieConsent = {
            analytics: true,
            timestamp: Date.now(),
        };
        await this.storageService.set('cookie-consent', consent);
        this.consentSubject.next(consent);
        this.showBannerSubject.next(false);
    }

    async rejectCookies(): Promise<void> {
        const consent: CookieConsent = {
            analytics: false,
            timestamp: Date.now(),
        };
        await this.storageService.set('cookie-consent', consent);
        this.consentSubject.next(consent);
        this.showBannerSubject.next(false);
    }

    hasConsent(): boolean {
        return this.consentSubject.value !== null;
    }

    hasAnalyticsConsent(): boolean {
        return this.consentSubject.value?.analytics === true;
    }
}
